import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_GROUPS = 'getGroups'
export const MANAGE_GROUP = 'manageGroup'
export const DELETE_GROUP = 'deleteGroup'

// mutation types
export const SAVE_GROUPS = 'saveGroups'
export const CLEAR_GROUPS = 'clearGroups'
export const SELECT_GROUP = 'selectGroup'
export const UNSELECT_GROUP = 'unselectGroup'
export const SAVE_MANAGE_GROUP_ERRORS = 'saveManageGroupErrors'
export const CLEAR_MANAGE_GROUP_ERRORS = 'clearManageGroupErrors'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_GROUPS = 'showToast/SettingsGroups'

export const DEFAULT_SELECTED_GROUP = {
  name: '',
  code: '',
}

const state = {
  groupsList: [],
  selectedGroup: { ...DEFAULT_SELECTED_GROUP },
  selectedGroupId: null,
  manageGroupErrors: [],

  responses: {
    groupsList: false,
    manageGroup: false,
    deleteGroup: false,
  },
}

const getters = {
  groupsList(state) {
    return state.groupsList
  },
  groupsEmpty(state) {
    return state.groupsList.length == 0
  },
  totalGroups(state) {
    return state.groupsList.length
  },
  selectedGroup(state) {
    return state.selectedGroup
  },
  selectedGroupsExist(state) {
    return state.groupsList.some(group => group._id == state.selectedGroupId)
  },
  groupsModalTitle(state) {
    return state.selectedGroupId ? 'Edit Group' : 'Add New Group'
  },
  manageGroupErrors(state) {
    return state.manageGroupErrors
  },
  manageGroupError: state => field => {
    return state.manageGroupErrors.filter(err => err.field == field)
  },
  groupsResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_GROUPS](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'groupsList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_GROUPS)
          .then(({ data }) => {
            context.commit(SAVE_GROUPS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_GROUPS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'groupsList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_GROUP](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageGroup', status: true })
        eventBus.$emit('startProgress')

        let method
        let url

        if (context.getters.selectedGroupsExist) {
          method = 'put'
          url = `${process.env.VUE_APP_GROUP}/${state.selectedGroupId}`
        } else {
          method = 'post'
          url = process.env.VUE_APP_GROUP
        }

        ApiService[method](url, state.selectedGroup)
          .then(({ data }) => {
            context.dispatch(GET_GROUPS)
            eventBus.$emit(TOAST_GROUPS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_GROUP_ERRORS, error.errors)
            eventBus.$emit(TOAST_GROUPS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageGroup', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_GROUP](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteGroup', status: true })
        eventBus.$emit('startProgress')

        ApiService.delete(`${process.env.VUE_APP_GROUP}/${state.selectedGroupId}`)
          .then(({ data }) => {
            context.dispatch(GET_GROUPS)
            eventBus.$emit(TOAST_GROUPS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_GROUPS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteGroup', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_GROUPS](state, groups) {
    state.groupsList = groups
  },
  [CLEAR_GROUPS](state) {
    state.groupsList = []
  },
  [SELECT_GROUP](state, group) {
    Object.keys(group).forEach(key => {
      state.selectedGroup[key] = group[key]
    })
    state.selectedGroupId = group._id
  },
  [UNSELECT_GROUP](state) {
    state.selectedGroup = { ...DEFAULT_SELECTED_GROUP }
    state.selectedGroupId = null
  },
  [SAVE_MANAGE_GROUP_ERRORS](state, errors) {
    state.manageGroupErrors = errors
  },
  [CLEAR_MANAGE_GROUP_ERRORS](state) {
    state.manageGroupErrors = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
